import React from 'react'
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react'
import { useParams } from 'react-router-dom'
import { EditIcon } from '@chakra-ui/icons'
import { FiTarget } from 'react-icons/fi'
import dayjs from 'dayjs'

import xhr from 'xhr'
import useAuth from 'hooks/useAuth'
import TransferProspectModal from '../../prospectos/TransferProspectModal'
import MasivoInput from '../../prospectos/MasivoInput'
import HistoryProspect from './HistoryProspect'
import ReminderProspect from './ReminderProspect'
import StudentsProspect from './students/StudentsProspect'
import useProgramTypes from '../../prospectos/useProgramTypes'
import { formatDays, loadClosingInfo } from '../../prospectos/daysToClose'
import { BRAND, InfoItem, PageHeader, SectionCard, StatusBadge } from '../../components/ui'
import {
  AD_SOURCES,
  ENTRY_CHANNELS,
  HOW_FOUND,
  PROSPECT_STATUS,
  channelLabel,
  contactRoleLabel,
  deviceLabel,
  hasCampaignData,
  firstResponseInfo,
  formatDuration,
  hasAutomaticCampaign,
  OBSERVATION_HELP,
  OBSERVATION_PLACEHOLDER,
  howFoundLabel,
  programLabel,
  statusInfo,
} from '../../prospectos/constants'

// "transferido" lo asigna la transferencia, no la asesora.
const EDITABLE_STATUSES = PROSPECT_STATUS.filter((el) => el.value !== 'transferido')

const isGoogleSource = (prospect) => Boolean(prospect.gclid) || /google/i.test(prospect.utm_source || '')

function VerProspecto() {
  const params = useParams()
  const id_est = params.id
  const toasts = useToast()
  const programTypes = useProgramTypes()
  const { user } = useAuth()
  // Cualquier asesora activa puede transferir (con motivo).
  const canTransfer = Boolean(user.adviser?.active)
  const [showTransfer, setShowTransfer] = React.useState(false)

  const [prospect, setProspect] = React.useState(null)
  const [fetching, isFetching] = React.useState(true)
  const [prospectState, setProspectState] = React.useState('')
  const [loading, setLoading] = React.useState(false)

  const [editingOrigin, setEditingOrigin] = React.useState(false)
  const [origin, setOrigin] = React.useState({
    contact_date: '',
    entry_channel: '',
    how_found: '',
    how_found_detail: '',
    observation: '',
    masivo: '',
  })
  const [savingOrigin, setSavingOrigin] = React.useState(false)
  // Fuente y anuncio que registra la asesora (mismos campos que las UTM, con utm_manual).
  const [editingAd, setEditingAd] = React.useState(false)
  const [savingAd, setSavingAd] = React.useState(false)
  const [adForm, setAdForm] = React.useState({ utm_source: '', utm_content: '' })
  const [studentsCount, setStudentsCount] = React.useState(null)
  const [closing, setClosing] = React.useState(undefined)

  const getProspect = React.useCallback(async () => {
    try {
      const res = await xhr(
        `/prospects/${id_est}?populate[adviser][populate]=user&populate[country]=true&populate[masivo]=true`
      )
      setProspect(res.data.data)
    } catch (error) {
      console.error(error)
    } finally {
      isFetching(false)
    }
  }, [id_est])

  React.useEffect(() => {
    getProspect()
  }, [getProspect])

  // Días entre el registro del prospecto y el primer pago de sus estudiantes.
  const loadClosing = React.useCallback(async (current) => {
    try {
      const info = await loadClosingInfo([current])
      setClosing(info[current.id] || null)
    } catch (error) {
      console.error(error)
      setClosing(null)
    }
  }, [])

  React.useEffect(() => {
    if (prospect?.id) loadClosing(prospect)
  }, [prospect?.id, prospect?.updatedAt, loadClosing])

  // Al registrar o vincular un estudiante, el backend pasa el prospecto a "registrado".
  const handleStudentsChange = ({ count, statusChanged }) => {
    if (typeof count === 'number') setStudentsCount(count)
    if (statusChanged) getProspect()
  }

  const handleState = async () => {
    if (!prospectState) return
    try {
      setLoading(true)

      const dataUpdate = { status: prospectState }
      if (prospectState === 'estudiando') dataUpdate.email_subscription = false

      const response = await xhr.put(`/prospects/${id_est}`, { data: dataUpdate })
      const saved = response.data.data
      setProspect({
        ...prospect,
        status: saved.status,
        first_status_change_at: saved.first_status_change_at,
        first_status: saved.first_status,
      })
      setProspectState('')

      toasts({ title: 'El estado se actualizó correctamente.', status: 'success', duration: 3000, isClosable: true })
    } catch (error) {
      console.error(error)
      toasts({ title: 'No se pudo actualizar el estado.', status: 'error', duration: 3000, isClosable: true })
    } finally {
      setLoading(false)
    }
  }

  const startEditOrigin = () => {
    setOrigin({
      contact_date: prospect.contact_date || '',
      entry_channel: prospect.entry_channel || '',
      how_found: prospect.how_found || 'pendiente',
      how_found_detail: prospect.how_found_detail || '',
      observation: prospect.observation || '',
      masivo: prospect.masivo?.name || '',
    })
    setEditingOrigin(true)
  }

  const startEditAd = () => {
    setAdForm({ utm_source: prospect.utm_source || '', utm_content: prospect.utm_content || '' })
    setEditingAd(true)
  }

  const handleSaveAd = async () => {
    const adId = adForm.utm_source ? adForm.utm_content.replace(/\s+/g, '') : ''
    if (adId && !/^[A-Za-z0-9_-]{1,64}$/.test(adId)) {
      toasts({
        title: 'El ID del anuncio solo puede tener letras, números, guiones y guiones bajos.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    try {
      setSavingAd(true)
      const response = await xhr.put(`/prospects/${id_est}`, {
        data: { utm_source: adForm.utm_source || null, utm_content: adId || null },
      })
      const updated = response.data.data
      setProspect({
        ...prospect,
        utm_source: updated.utm_source,
        utm_content: updated.utm_content,
        utm_manual: updated.utm_manual,
      })
      setEditingAd(false)
      toasts({ title: 'Anuncio guardado', status: 'success', duration: 2500, isClosable: true })
    } catch (error) {
      console.error(error)
      toasts({
        title: error?.response?.data?.error?.message || 'No se pudo guardar el anuncio.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setSavingAd(false)
    }
  }

  const handleSaveOrigin = async () => {
    const detail = origin.how_found === 'otro' ? origin.how_found_detail.trim() : ''
    if (origin.how_found === 'otro' && !detail) {
      toasts({ title: 'Escribe cómo conoció Klazia.', status: 'warning', duration: 3000, isClosable: true })
      return
    }
    if (origin.contact_date && origin.contact_date > dayjs().format('YYYY-MM-DD')) {
      toasts({ title: 'La fecha de contacto no puede ser futura.', status: 'warning', duration: 3000, isClosable: true })
      return
    }
    try {
      setSavingOrigin(true)
      const data = {
        contact_date: origin.contact_date || null,
        how_found: origin.how_found || null,
        how_found_detail: detail ? detail.slice(0, 120) : null,
        observation: origin.observation.trim() || null,
      }
      if (origin.entry_channel) data.entry_channel = origin.entry_channel

      const response = await xhr.put(`/prospects/${id_est}`, { data })
      const updated = response.data.data
      let masivo = prospect.masivo || null
      // El masivo se guarda aparte: el API lo crea si no existe.
      if (origin.masivo.trim() !== (prospect.masivo?.name || '')) {
        const resMasivo = await xhr.put(`/prospects/${id_est}/masivo`, { data: { name: origin.masivo.trim() } })
        masivo = resMasivo.data.masivo
      }
      setProspect({
        ...prospect,
        contact_date: updated.contact_date,
        entry_channel: updated.entry_channel,
        how_found: updated.how_found,
        how_found_detail: updated.how_found_detail,
        observation: updated.observation,
        masivo,
      })
      setEditingOrigin(false)
      toasts({ title: 'Origen actualizado.', status: 'success', duration: 3000, isClosable: true })
    } catch (error) {
      console.error(error)
      toasts({ title: 'No se pudo guardar el origen.', status: 'error', duration: 3000, isClosable: true })
    } finally {
      setSavingOrigin(false)
    }
  }

  if (fetching) {
    return (
      <Flex justify="center" py={16}>
        <Spinner color={BRAND} />
      </Flex>
    )
  }

  if (!prospect) {
    return <Text color="gray.600">No se encontró el prospecto #{id_est}.</Text>
  }

  const status = statusInfo(prospect.status)
  const adviserUser = prospect.adviser?.user
  const programName = programLabel(prospect.program, programTypes)
  const phone = `${prospect.country?.phone_code ? `+${prospect.country.phone_code} ` : ''}${prospect.cellphone || ''}`
  const subtitle = [
    `Prospecto #${prospect.id}`,
    contactRoleLabel(prospect.contact_role),
    phone.trim(),
    `Ingreso: ${dayjs(prospect.createdAt).format('DD/MM/YYYY h:mm A')}`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Box>
      <PageHeader
        breadcrumb={[{ label: 'Escritorio', to: '/plataforma/asesoras' }, { label: 'Ver prospecto' }]}
        title={
          <Flex as="span" align="center" gap={3} wrap="wrap">
            <Flex
              as="span"
              w="44px"
              h="44px"
              borderRadius="full"
              bg="purple.50"
              color={BRAND}
              fontWeight="700"
              fontSize="md"
              align="center"
              justify="center"
              flexShrink={0}
            >
              {(prospect.name || '?')
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((word) => word[0].toUpperCase())
                .join('')}
            </Flex>
            {prospect.name}
            <StatusBadge label={status.label} color={status.color} />
          </Flex>
        }
        subtitle={subtitle}
        actions={
          <Flex gap={2} align="center">
            <Select
              size="sm"
              bg="white"
              w="220px"
              placeholder="Cambiar estado"
              value={prospectState}
              onChange={(e) => setProspectState(e.target.value)}
            >
              {EDITABLE_STATUSES.map((el) => (
                <option key={el.value} value={el.value}>
                  {el.label}
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              bg={BRAND}
              color="white"
              _hover={{ bg: '#463fd1' }}
              isDisabled={!prospectState}
              isLoading={loading}
              onClick={handleState}
            >
              Actualizar
            </Button>
            {canTransfer && (
              <Button size="sm" variant="outline" colorScheme="purple" onClick={() => setShowTransfer(true)}>
                Transferir
              </Button>
            )}
          </Flex>
        }
      />

      <Grid templateColumns={{ base: '1fr', xl: '2fr 1fr' }} gap={4} mb={6} alignItems="start">
        <SectionCard
          title="Resumen"
          actions={
            !editingOrigin && (
              <Button size="sm" variant="ghost" colorScheme="purple" leftIcon={<EditIcon />} onClick={startEditOrigin}>
                Editar origen
              </Button>
            )
          }
        >
          <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={5}>
            <InfoItem label="Programa de interés">
              {programName}
              {prospect.program && !programTypes.some((el) => el.slug === prospect.program) && programName !== 'Por definir' && (
                <Text fontSize="xs" color="gray.400" fontWeight="400" wordBreak="break-all">
                  {prospect.program}
                </Text>
              )}
            </InfoItem>
            <InfoItem label="Asesora de la venta">
              {adviserUser ? `${adviserUser.first_name || ''} ${adviserUser.last_name || ''}`.trim() : null}
            </InfoItem>
            <InfoItem label="Correo">{prospect.email}</InfoItem>
            <InfoItem label="Días para cerrar">
              {closing === undefined ? (
                <Text as="span" color="gray.400" fontWeight="400">
                  Calculando…
                </Text>
              ) : closing?.daysToClose !== null && closing?.daysToClose !== undefined ? (
                <>
                  {formatDays(closing.daysToClose)}
                  <Text fontSize="xs" color="gray.500" fontWeight="400">
                    Primer pago: {dayjs(closing.firstPaymentDate).format('DD/MM/YYYY')}
                  </Text>
                </>
              ) : (
                <Text as="span" color="gray.400" fontWeight="400">
                  {closing?.hasStudents ? 'Sin pago registrado' : 'Sin estudiante vinculado'}
                </Text>
              )}
            </InfoItem>

            {editingOrigin ? (
              <>
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.500">
                    Fecha de contacto
                  </FormLabel>
                  <Input
                    id="origin-contact-date"
                    type="date"
                    size="sm"
                    max={dayjs().format('YYYY-MM-DD')}
                    value={origin.contact_date}
                    onChange={(e) => setOrigin({ ...origin, contact_date: e.target.value })}
                  />
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Día en que el contacto escribió. Vacía, se usa la fecha de registro.
                  </Text>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.500">
                    Canal de ingreso
                  </FormLabel>
                  <Select
                    id="origin-entry-channel"
                    size="sm"
                    placeholder={prospect.entry_channel ? undefined : 'Formulario web (sin dato)'}
                    value={origin.entry_channel}
                    onChange={(e) => setOrigin({ ...origin, entry_channel: e.target.value })}
                  >
                    {ENTRY_CHANNELS.map((el) => (
                      <option key={el.value} value={el.value}>
                        {el.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.500">
                    Cómo nos conoció
                  </FormLabel>
                  <Select
                    id="origin-how-found"
                    size="sm"
                    value={origin.how_found}
                    onChange={(e) => setOrigin({ ...origin, how_found: e.target.value })}
                  >
                    {HOW_FOUND.map((el) => (
                      <option key={el.value} value={el.value}>
                        {el.label}
                      </option>
                    ))}
                  </Select>
                  {origin.how_found === 'otro' && (
                    <Input
                      id="origin-how-found-detail"
                      size="sm"
                      mt={2}
                      maxLength={120}
                      placeholder="¿Cuál?"
                      value={origin.how_found_detail}
                      onChange={(e) => setOrigin({ ...origin, how_found_detail: e.target.value })}
                    />
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.500">
                    Reactivado por masivo
                  </FormLabel>
                  <MasivoInput value={origin.masivo} onChange={(masivo) => setOrigin({ ...origin, masivo })} />
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Solo si el contacto respondió a un masivo. Déjalo vacío si no.
                  </Text>
                </FormControl>
              </>
            ) : (
              <>
                <InfoItem label="Fecha de contacto">
                  {prospect.contact_date ? (
                    dayjs(prospect.contact_date).format('DD/MM/YYYY')
                  ) : (
                    <Text as="span" color="gray.500" fontWeight="400">
                      {dayjs(prospect.createdAt).format('DD/MM/YYYY h:mm A')} (registro)
                    </Text>
                  )}
                </InfoItem>
                <InfoItem label="Canal de ingreso">{channelLabel(prospect)}</InfoItem>
                <InfoItem label="Cómo nos conoció">
                  {prospect.how_found ? (
                    <Text as="span" color={prospect.how_found === 'pendiente' ? 'orange.500' : 'gray.800'}>
                      {howFoundLabel(prospect.how_found, prospect.how_found_detail)}
                    </Text>
                  ) : null}
                </InfoItem>
                {prospect.masivo?.name && <InfoItem label="Reactivado por masivo">{prospect.masivo.name}</InfoItem>}
              </>
            )}
            <InfoItem label="Contactar por">{prospect.from}</InfoItem>
            <InfoItem label="Primera gestión">
              {(() => {
                const info = firstResponseInfo(prospect)
                if (info.state === 'done') {
                  return (
                    <>
                      {formatDuration(info.minutes)}
                      <Text fontSize="xs" color="gray.500" fontWeight="400">
                        A «{statusInfo(prospect.first_status).label}» el{' '}
                        {dayjs(prospect.first_status_change_at).format('DD/MM/YYYY h:mm A')}
                      </Text>
                    </>
                  )
                }
                if (info.state === 'pending') {
                  return (
                    <Text as="span" color="orange.500">
                      Sin gestionar · lleva {formatDuration(info.minutes)}
                    </Text>
                  )
                }
                return null
              })()}
            </InfoItem>
            {prospect.transfer_reason && (
              <InfoItem label="Motivo de transferencia">{prospect.transfer_reason}</InfoItem>
            )}

            {(prospect.student_age || prospect.last_grade) && (
              <>
                <InfoItem label="Edad del estudiante">{prospect.student_age}</InfoItem>
                <InfoItem label="Último grado aprobado">{prospect.last_grade}</InfoItem>
              </>
            )}
            {prospect.name_parent && <InfoItem label="Acudiente">{prospect.name_parent}</InfoItem>}

            <Box gridColumn={{ md: 'span 3' }}>
              {editingOrigin ? (
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.500">
                    Observación
                  </FormLabel>
                  <Textarea
                    id="origin-observation"
                    size="sm"
                    rows={3}
                    placeholder={OBSERVATION_PLACEHOLDER}
                    value={origin.observation}
                    onChange={(e) => setOrigin({ ...origin, observation: e.target.value })}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {OBSERVATION_HELP}
                  </Text>
                  <Flex gap={2} justify="flex-end" mt={3}>
                    <Button size="sm" variant="ghost" onClick={() => setEditingOrigin(false)}>
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      bg={BRAND}
                      color="white"
                      _hover={{ bg: '#463fd1' }}
                      isLoading={savingOrigin}
                      onClick={handleSaveOrigin}
                    >
                      Guardar origen
                    </Button>
                  </Flex>
                </FormControl>
              ) : (
                <InfoItem label="Observación">
                  {prospect.observation && <Text whiteSpace="pre-wrap">{prospect.observation}</Text>}
                </InfoItem>
              )}
            </Box>
          </Grid>
        </SectionCard>

        <SectionCard
          title="Campaña"
          actions={
            !editingAd &&
            !hasAutomaticCampaign(prospect) && (
              <Button size="sm" variant="ghost" color={BRAND} leftIcon={<EditIcon />} onClick={startEditAd}>
                {prospect.utm_manual ? 'Editar anuncio' : 'Registrar anuncio'}
              </Button>
            )
          }
        >
          {editingAd ? (
            <Grid templateColumns="1fr" gap={3}>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.500">
                  Fuente
                </FormLabel>
                <Select
                  id="ad-source"
                  size="sm"
                  placeholder="No vino de un anuncio"
                  value={adForm.utm_source}
                  onChange={(e) => setAdForm({ ...adForm, utm_source: e.target.value })}
                >
                  {AD_SOURCES.map((el) => (
                    <option key={el.value} value={el.value}>
                      {el.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.500">
                  Anuncio
                </FormLabel>
                <Input
                  id="ad-id"
                  size="sm"
                  maxLength={64}
                  placeholder="Ej.: 120212345678900123"
                  isDisabled={!adForm.utm_source}
                  value={adForm.utm_content}
                  onChange={(e) => setAdForm({ ...adForm, utm_content: e.target.value })}
                />
                <Text fontSize="xs" color="gray.400" mt={1}>
                  ID del anuncio: cópialo de HiBot o del anuncio. Opcional.
                </Text>
              </FormControl>
              <Flex gap={2} justify="flex-end">
                <Button size="sm" variant="ghost" onClick={() => setEditingAd(false)}>
                  Cancelar
                </Button>
                <Button size="sm" bg={BRAND} color="white" _hover={{ bg: '#463fd1' }} isLoading={savingAd} onClick={handleSaveAd}>
                  Guardar anuncio
                </Button>
              </Flex>
            </Grid>
          ) : hasCampaignData(prospect) ? (
            <Box>
              {prospect.utm_manual && (
                <Badge colorScheme="purple" variant="subtle" textTransform="none" fontWeight="600" mb={3}>
                  Registrado por asesora
                </Badge>
              )}
                <Grid templateColumns="1fr" gap={3}>
                  <InfoItem label="Fuente">{prospect.utm_source}</InfoItem>
                  {!prospect.utm_manual && <InfoItem label="Medio">{prospect.utm_medium}</InfoItem>}
                  {!prospect.utm_manual && <InfoItem label="Campaña">{prospect.utm_campaign}</InfoItem>}
                  <InfoItem label="Anuncio">
                    {prospect.utm_content}
                    {/^\d{8,}$/.test(prospect.utm_content || '') && (
                      <Text fontSize="xs" color="gray.400" fontWeight="400">
                        {isGoogleSource(prospect) ? 'ID del anuncio en Google Ads' : 'ID del anuncio en Meta Ads'}
                      </Text>
                    )}
                  </InfoItem>
                  {!prospect.utm_manual && <InfoItem label="Palabra clave">{prospect.utm_term}</InfoItem>}
                  <InfoItem label="Dispositivo">{deviceLabel(prospect.utm_device)}</InfoItem>
                  {prospect.gclid && (
                    <InfoItem label="Clic de Google Ads">
                      <Text fontSize="xs" color="gray.500" fontWeight="400" wordBreak="break-all">
                        {prospect.gclid}
                      </Text>
                    </InfoItem>
                  )}
                </Grid>
            </Box>
          ) : (
            <Flex direction="column" align="center" textAlign="center" py={6} gap={2}>
              <Flex boxSize="44px" borderRadius="full" bg="purple.50" color={BRAND} align="center" justify="center">
                <FiTarget size={20} />
              </Flex>
              <Text fontSize="sm" fontWeight="600" color="gray.700">
                Sin datos de campaña
              </Text>
              <Text fontSize="xs" color="gray.500" maxW="260px">
                Llegan solos cuando el contacto se registra en el formulario web desde un anuncio. Si escribió desde un
                anuncio por WhatsApp, usa «Registrar anuncio».
              </Text>
              {prospect.utm_device && (
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Se registró desde: <b>{deviceLabel(prospect.utm_device)}</b>
                </Text>
              )}
            </Flex>
          )}
        </SectionCard>
      </Grid>

      <SectionCard padding={0}>
        <Tabs colorScheme="purple">
          <TabList px={5} pt={2}>
            <Tab fontWeight="500">Historial</Tab>
            <Tab fontWeight="500">Recordatorios</Tab>
            <Tab fontWeight="500">Estudiantes{studentsCount !== null ? ` (${studentsCount})` : ''}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={5} py={5}>
              {/* Se remonta cuando el prospecto cambia para mostrar los registros automáticos. */}
              <HistoryProspect key={prospect.updatedAt} />
            </TabPanel>
            <TabPanel px={5} py={5}>
              <ReminderProspect />
            </TabPanel>
            <TabPanel px={5} py={5}>
              <StudentsProspect prospect={prospect} onChange={handleStudentsChange} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </SectionCard>

      <TransferProspectModal
        prospect={prospect}
        isOpen={showTransfer}
        onClose={() => setShowTransfer(false)}
        onTransferred={getProspect}
      />
    </Box>
  )
}

export default VerProspecto
